import type { QueryClient } from '@tanstack/react-query';
import type {
  ChatTopicKind,
  ChatTopicListProjection,
  ChatTopicProjection,
  ChatTopicUnreadProjection,
  DmThreadProjection,
} from '@projoint/shared';

import {
  CHAT_LIST_KEY,
  applyIncomingMessage,
  scheduleChatListRefresh,
  type ChatListData,
} from '@/shared/api/chat-list-cache';
import { isActiveDm, isActiveTopic } from '@/shared/lib/active-chat';

/**
 * Chat realtime → cache, without refetch storms (Telegram-style Chat):
 *
 *  - `chat.message_created` / `dm.message_created` carry the row bits of the
 *    message (team, channel, author, ≤140-char preview, mentions) to exactly
 *    the people who can read it.  The Chat list, the team's channel list and
 *    its per-channel badges are PATCHED in place: the row jumps to the top,
 *    the badge counts up.  Only the open conversation's messages refetch.
 *  - Anything the patch can't express (an unknown chat, an edit / delete that
 *    may change a preview, my pin / mute / read on another device) schedules
 *    ONE debounced reconcile of the list (`scheduleChatListRefresh`).
 */

type Payload = Record<string, unknown>;
const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);

function meId(qc: QueryClient): string | undefined {
  return qc.getQueryData<{ id?: string }>(['me'])?.id;
}

function isTopicListKey(key: readonly unknown[]): boolean {
  return key[0] === 'chat' && key[2] === 'topics' && key.length === 3;
}

/** Newest-message bits on the channel inside whichever team list holds it. */
function patchTopicLists(qc: QueryClient, topicId: string, p: Payload): boolean {
  let found = false;
  for (const [key, list] of qc.getQueriesData<ChatTopicListProjection>({
    predicate: (q) => isTopicListKey(q.queryKey),
  })) {
    if (!list) continue;
    const touch = (t: ChatTopicProjection): ChatTopicProjection => {
      if (t.id !== topicId) return t;
      found = true;
      return {
        ...t,
        lastMessageAt: str(p.createdAt) ?? t.lastMessageAt,
        lastMessagePreview: str(p.preview),
        lastMessageAuthorName: str(p.authorName),
      };
    };
    const next: ChatTopicListProjection = {
      general: list.general ? touch(list.general) : null,
      projects: list.projects.map(touch),
      custom: list.custom.map(touch),
      archived: list.archived.map(touch),
    };
    if (found) {
      qc.setQueryData(key, next);
      return true;
    }
  }
  return false;
}

/** +1 on the channel's badge (and @) in the forum's unread list. */
function patchTopicUnread(qc: QueryClient, topicId: string, mentionsMe: boolean): void {
  for (const [key, list] of qc.getQueriesData<ChatTopicUnreadProjection[]>({
    predicate: (q) => q.queryKey[0] === 'chat' && q.queryKey[2] === 'topics' && q.queryKey[3] === 'unread',
  })) {
    if (!list?.some((u) => u.topicId === topicId)) continue;
    qc.setQueryData<ChatTopicUnreadProjection[]>(
      key,
      list.map((u) =>
        u.topicId === topicId
          ? { ...u, unreadCount: u.unreadCount + 1, hasMention: u.hasMention || mentionsMe }
          : u,
      ),
    );
    return;
  }
}

function patchList(qc: QueryClient, msg: Parameters<typeof applyIncomingMessage>[1]): void {
  const data = qc.getQueryData<ChatListData>(CHAT_LIST_KEY);
  if (!data) return; // not loaded yet — the first fetch brings it in
  const { data: next, found } = applyIncomingMessage(data, msg, meId(qc));
  if (found) qc.setQueryData(CHAT_LIST_KEY, next);
  else scheduleChatListRefresh(qc, 300); // a chat I don't have yet
}

export function applyChatMessageCreated(qc: QueryClient, p: Payload): void {
  const topicId = str(p.topicId);
  const teamId = str(p.teamId);
  const messageId = str(p.messageId);
  const authorId = str(p.authorId);
  const parentMessageId = str(p.parentMessageId);
  if (topicId) {
    void qc.invalidateQueries({ queryKey: ['chat', 'topic', topicId, 'messages'] });
    if (parentMessageId) {
      void qc.invalidateQueries({
        queryKey: ['chat', 'topic', topicId, 'thread', parentMessageId, 'messages'],
      });
    }
  }
  void qc.invalidateQueries({ queryKey: ['me', 'unread-summary'] });
  if (!topicId || !teamId || !messageId || !authorId) {
    // An older server: no row bits — fall back to refetching.
    void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'chat' && q.queryKey[2] === 'topics' });
    scheduleChatListRefresh(qc, 300);
    return;
  }
  const me = meId(qc);
  const mentioned = Array.isArray(p.mentionedUserIds) ? (p.mentionedUserIds as unknown[]) : [];
  const mentionsMe = !!me && mentioned.includes(me);
  const active = isActiveTopic(topicId);
  if (!patchTopicLists(qc, topicId, p)) {
    void qc.invalidateQueries({ predicate: (q) => isTopicListKey(q.queryKey) });
  }
  if (me && authorId !== me && !active) patchTopicUnread(qc, topicId, mentionsMe);
  patchList(qc, {
    kind: 'team',
    chatId: teamId,
    topicId,
    topicName: str(p.topicName),
    topicKind: (str(p.topicKind) as ChatTopicKind | null) ?? null,
    messageId,
    authorId,
    authorName: str(p.authorName),
    preview: str(p.preview),
    createdAt: str(p.createdAt) ?? new Date().toISOString(),
    mentionsMe,
    active,
  });
}

export function applyDmMessageCreated(qc: QueryClient, p: Payload): void {
  const threadId = str(p.threadId);
  const messageId = str(p.messageId);
  const authorId = str(p.authorId);
  void qc.invalidateQueries({ queryKey: ['me', 'unread-summary'] });
  if (!threadId) return;
  void qc.invalidateQueries({ queryKey: ['dms', 'thread', threadId, 'messages'] });
  if (!messageId || !authorId) {
    void qc.invalidateQueries({ queryKey: ['dms', 'threads'] });
    scheduleChatListRefresh(qc, 300);
    return;
  }
  const me = meId(qc);
  const active = isActiveDm(threadId);
  const createdAt = str(p.createdAt) ?? new Date().toISOString();
  // The thread list (phone conversation screens look the thread up there).
  const threads = qc.getQueryData<DmThreadProjection[]>(['dms', 'threads']);
  if (threads?.some((t) => t.id === threadId)) {
    qc.setQueryData<DmThreadProjection[]>(['dms', 'threads'], (old) =>
      old?.map((t) =>
        t.id === threadId
          ? {
              ...t,
              lastMessageAt: createdAt,
              lastMessagePreview: str(p.preview),
              unreadCount: t.unreadCount + (me && authorId !== me && !active ? 1 : 0),
            }
          : t,
      ),
    );
  } else {
    void qc.invalidateQueries({ queryKey: ['dms', 'threads'] });
  }
  patchList(qc, {
    kind: 'dm',
    chatId: threadId,
    messageId,
    authorId,
    authorName: str(p.authorName),
    preview: str(p.preview),
    createdAt,
    mentionsMe: false,
    active,
  });
}

/** Edit / delete may change a preview; reactions never do. */
export function applyChatMessageChanged(qc: QueryClient, type: string, p: Payload): void {
  const topicId = str(p.topicId);
  if (topicId) void qc.invalidateQueries({ queryKey: ['chat', 'topic', topicId, 'messages'] });
  if (type === 'chat.message_reacted') return;
  void qc.invalidateQueries({ predicate: (q) => isTopicListKey(q.queryKey) });
  if (type === 'chat.message_deleted') {
    void qc.invalidateQueries({
      predicate: (q) => q.queryKey[0] === 'chat' && q.queryKey[2] === 'topics' && q.queryKey[3] === 'unread',
    });
    void qc.invalidateQueries({ queryKey: ['me', 'unread-summary'] });
  }
  scheduleChatListRefresh(qc);
}

export function applyDmMessageChanged(qc: QueryClient, p: Payload): void {
  const threadId = str(p.threadId);
  if (threadId) void qc.invalidateQueries({ queryKey: ['dms', 'thread', threadId, 'messages'] });
  void qc.invalidateQueries({ queryKey: ['dms', 'threads'] });
  void qc.invalidateQueries({ queryKey: ['me', 'unread-summary'] });
  scheduleChatListRefresh(qc);
}

/** My pin / mute / "mark all read" from another tab or device. */
export function applyMyChatsChanged(qc: QueryClient): void {
  scheduleChatListRefresh(qc, 400);
  void qc.invalidateQueries({
    predicate: (q) => q.queryKey[0] === 'chat' && q.queryKey[2] === 'topics' && q.queryKey[3] === 'unread',
  });
}
